import React, { useMemo, useContext, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Camera, Layers, Trash2 } from 'react-feather'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import _ from 'lodash'

import { FILE_SIZE, SUPPORTED_FORMATS } from '~/constants/image'
import { Show } from './commons'
import { ImagesFormContext } from '~/pages/app/locations/BulkEdit/EditInformations/ImagesForm'
import { toast } from 'react-toastify'
import { humanFileSize } from '~/utility/byteSize'

function DropzoneImageInput({
  id,
  onChange,
  value: _value = [],
  initialValue = [],
  invalid,
  multiple = false,
  controller,
  name,
  switchId,
  maxFileSize = FILE_SIZE,
  altBehavior = false,
  ...props
}) {
  const [files, setFiles] = useState(initialValue)

  const imagesFormContext = useContext(ImagesFormContext)
  const editableFields = imagesFormContext
    ? imagesFormContext.editableFields
    : null

  const { getRootProps, getInputProps } = useDropzone({
    accept: SUPPORTED_FORMATS.join(', '),
    onDrop: async (acceptedFiles, fileRejections) => {
      try {
        if (fileRejections.length) {
          for (const file of fileRejections) {
            if (file.size > maxFileSize) {
              toast.error(
                <FormattedMessage
                  id="File is too large"
                  values={{ size: humanFileSize(maxFileSize) }}
                />
              )
            }
            if (!SUPPORTED_FORMATS.includes(file.type)) {
              toast.error(
                <FormattedMessage id="File type not supported. Please upload a JPG or PNG file." />
              )
            }
          }
        }
        const newAcceptedFiles = []
        if (acceptedFiles.length) {
          for (const file of acceptedFiles) {
            const img = new Image()
            img.src = URL.createObjectURL(file)
            await img.decode()
            const height = img.height
            const width = img.width
            if (width < 400 || height < 300) {
              toast.error(
                <FormattedMessage id="Image is too small. Minimum size is 400px by 300px." />
              )
            } else {
              newAcceptedFiles.push(file)
            }
          }
          const resolvedFiles = newAcceptedFiles.map(file =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              id: `${_.uniqueId()}-${new Date().getTime()}`,
            })
          )

          if (multiple) {
            setFiles(prev => prev.concat(...resolvedFiles))
            onChange(files.concat(resolvedFiles))
          } else {
            setFiles(resolvedFiles)
            onChange(resolvedFiles)
          }
        }
      } catch (error) {
        console.error(error)
        toast.error(
          <FormattedMessage id="An error occurred while uploading the image." />
        )
      }
    },
    maxSize: maxFileSize,
    multiple: multiple,
  })

  useMemo(() => {
    if (editableFields && !editableFields[switchId]) {
      setFiles([])
    }
  }, [editableFields, switchId])

  const thumbs = files.map(file => (
    <div className="dz-thumb position-relative" key={file.id}>
      <CloseIcon
        size={14}
        className="text-danger"
        onClick={() => {
          setFiles(prev => prev?.filter(f => f.id !== file.id) || [])

          const files = controller?.values[name]
          return controller?.setFieldValue(
            name,
            files?.filter(f => f.id !== file.id) || []
          )
        }}
      />
      <div className="dz-thumb-inner">
        <img src={file.preview} className="dz-img" alt={file.name} />
      </div>
    </div>
  ))

  useEffect(
    () => () => {
      if (!multiple) files.forEach(file => URL.revokeObjectURL(file.preview))
    },
    [files, multiple]
  )

  useEffect(() => {
    if (_value && altBehavior) setFiles(_value)
  }, [_value])

  return (
    <section>
      <div
        {...getRootProps({
          className: `dropzone`,
          style: {
            cursor: 'pointer',
            borderColor: invalid ? 'red' : '#9E9E9E',
            opacity: props.disabled ? '0.4' : '1',
          },
        })}
      >
        <input id={id} {...props} {...getInputProps()} />
        <em style={{ color: '#9E9E9E', padding: 15 }}>
          {multiple && !altBehavior && (
            <>
              <Layers /> <FormattedMessage id="Upload multiple files here." />
            </>
          )}
          {!multiple && !altBehavior && (
            <>
              <Camera />{' '}
              <FormattedMessage id="Click to upload or drag and drop your image here." />
            </>
          )}
          {Boolean(altBehavior && !files?.length) && (
            <div className="d-flex flex-column align-items-center">
              <div
                style={{ color: '#7367F0', gap: '0.5rem' }}
                className="d-flex align-items-center"
              >
                <Camera color="#7367F0" />
                <FormattedMessage id="Click or drag an image here" />
              </div>
              <div>
                <FormattedMessage id="16:9 format recommended. PNG or JPG 2MB max size." />
              </div>
            </div>
          )}

          {Boolean(altBehavior && files?.length) && <aside>{thumbs}</aside>}
        </em>
      </div>
      {!altBehavior && (
        <Show condition={files?.length}>
          <aside>{thumbs}</aside>
        </Show>
      )}
    </section>
  )
}

const CloseIcon = styled(Trash2)`
  position: absolute;
  right: 5px;
  top: 5px;
  cursor: pointer;
`

export default DropzoneImageInput
